import { useCallback, useEffect, useRef } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import type {
	AiSuggestionsEntryPoint,
	AiSuggestionsEventPayload,
	AiSuggestionsRightRailEntryPoint,
} from '@atlaskit/editor-common/analytics';
import { ACTION, ACTION_SUBJECT } from '@atlaskit/editor-common/analytics/types/enums';

import type { Suggestion } from '@atlassian/editor-plugin-ai-suggestions';

export const SUGGESTIONS_PANEL_ANALYTICS_SOURCE = 'suggestionsPanel';

export const SUGGESTIONS_PANEL_EMPTY_STATE_ENTRY_POINT: AiSuggestionsEntryPoint =
	'suggestionsPanelEmptyState';

type SuggestionAnalyticsSnapshot = {
	actionKind?: string;
	toolCalls?: Array<{
		arguments: {
			localId?: string;
			localIds?: string[];
		};
		name: string;
	}>;
	type: string;
};

type RightRailViewedTrackData = Omit<
	Extract<AiSuggestionsEventPayload, { action: ACTION.RIGHT_RAIL_VIEWED }>,
	'eventType'
> & {
	source: typeof SUGGESTIONS_PANEL_ANALYTICS_SOURCE;
};

const getAffectedBlocks = (suggestion: SuggestionAnalyticsSnapshot): number => {
	const localIds =
		suggestion.toolCalls?.flatMap((toolCall) =>
			toolCall.name === 'removeNodes'
				? (toolCall.arguments.localIds ?? [])
				: toolCall.arguments.localId
					? [toolCall.arguments.localId]
					: [],
		) ?? [];

	return new Set(localIds).size;
};

export const useSuggestionsEmptyStateExposureAnalytics = ({
	enabled = true,
	entryPoint,
}: {
	enabled?: boolean;
	entryPoint: AiSuggestionsEntryPoint;
}): void => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const hasFiredRef = useRef(false);

	useEffect(() => {
		if (!enabled || hasFiredRef.current) {
			return;
		}

		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				source: SUGGESTIONS_PANEL_ANALYTICS_SOURCE,
				action: ACTION.EXPOSED,
				actionSubject: ACTION_SUBJECT.AI_SUGGESTIONS,
				attributes: {
					entryPoint,
				},
			},
		}).fire();
		hasFiredRef.current = true;
	}, [createAnalyticsEvent, enabled, entryPoint]);
};

export const useReasoningViewedAnalytics = (): ((suggestion: Suggestion) => void) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	return useCallback(
		(suggestion: Suggestion) => {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					source: SUGGESTIONS_PANEL_ANALYTICS_SOURCE,
					action: ACTION.REASONING_VIEWED,
					actionSubject: ACTION_SUBJECT.AI_SUGGESTIONS,
					attributes: {
						affectedBlocks: getAffectedBlocks(suggestion),
						interactionPoint: 'sidebar',
						reasoningCharacterCount: suggestion.reasoning?.length ?? 0,
						suggestionType: suggestion.type,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent],
	);
};

export const useSuggestionsPanelOpenAnalytics = ({
	entryPoint,
	isFullPanelLoading,
	suggestions,
}: {
	entryPoint: AiSuggestionsRightRailEntryPoint;
	isFullPanelLoading: boolean;
	suggestions?: readonly SuggestionAnalyticsSnapshot[];
}): void => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const hasFiredRef = useRef(false);

	useEffect(() => {
		if (hasFiredRef.current || isFullPanelLoading) {
			return;
		}

		const suggestionDetails = (suggestions ?? []).map((suggestion) => ({
			suggestionType: suggestion.type,
			actionKind: suggestion.actionKind ?? 'SUGGESTED_EDIT',
			affectedBlocks: getAffectedBlocks(suggestion),
		}));
		const data = {
			source: SUGGESTIONS_PANEL_ANALYTICS_SOURCE,
			action: ACTION.RIGHT_RAIL_VIEWED,
			actionSubject: ACTION_SUBJECT.AI_SUGGESTIONS,
			attributes: {
				entryPoint,
				numberOfSuggestions: suggestionDetails.length,
				suggestionDetails,
			},
		} satisfies RightRailViewedTrackData;

		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data,
		}).fire();
		hasFiredRef.current = true;
	}, [createAnalyticsEvent, entryPoint, isFullPanelLoading, suggestions]);
};
